import React from "react";

/**
 * Premium Reddy Club brand lockup — actual logo image + wordmark + ROYAL pill + tagline.
 * Static (no animations). Variants: full / stacked / compact.
 * Theme: Dark Navy base · Cyan Neon glow ring · Gold wordmark.
 */
export default function ReddyBrand({ variant = "full", size = 44 }) {
  const stacked = variant === "stacked";
  const compact = variant === "compact";
  const badgeSize = stacked ? 110 : size;
  // Compact (header) must stay small on tiny phones so wallet pill doesn't overflow
  const titleSize = stacked ? "text-2xl" : compact ? "text-sm sm:text-base" : "text-xl";

  return (
    <div
      className={`inline-flex ${stacked ? "flex-col items-center gap-2" : "items-center gap-1.5 sm:gap-2.5"} leading-none min-w-0`}
      data-testid="reddy-brand"
    >
      <ReddyBadge size={badgeSize} />
      <div className={`${stacked ? "flex flex-col items-center" : "flex flex-col items-start"} leading-none min-w-0`}>
        <div className={`${stacked ? "flex items-center gap-2" : "flex items-center gap-1 sm:gap-1.5"} min-w-0`}>
          <span
            className={`font-display font-black ${titleSize} tracking-tight text-gold-static whitespace-nowrap truncate`}
            style={{ letterSpacing: "0.02em" }}
          >
            REDDY&nbsp;CLUB
          </span>
          {/* Hide ROYAL pill on very narrow screens so wallet never overflows */}
          <span className={compact ? "hidden sm:inline-flex" : "inline-flex"}>
            <RoyalPill />
          </span>
        </div>
        {variant !== "compact" && (
          <div className={`${stacked ? "mt-1.5" : "mt-0.5"} text-[9px] font-black tracking-[0.3em] uppercase inline-flex items-center gap-1`}>
            <span className="w-1 h-1 rounded-full bg-cyan-300 inline-block shadow-[0_0_6px_rgba(34,211,238,0.9)]" />
            <span className="text-cyan-200/90">Play</span>
            <span className="text-amber-300">·</span>
            <span className="text-cyan-200/90">Win</span>
            <span className="text-amber-300">·</span>
            <span className="text-cyan-200/90">Repeat</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** Circular logo image inside a premium cyan-neon glow ring for 3D depth. */
export function ReddyBadge({ size = 44 }) {
  const ringPad = Math.max(2, Math.round(size * 0.05));
  return (
    <div
      className="relative shrink-0 rounded-full"
      style={{
        width: size,
        height: size,
        padding: ringPad,
        background:
          "conic-gradient(from 220deg, #22d3ee 0deg, #67e8f9 90deg, #22d3ee 180deg, #0891b2 270deg, #22d3ee 360deg)",
        boxShadow:
          "0 0 18px rgba(34,211,238,0.55), 0 0 44px rgba(34,211,238,0.22), inset 0 0 8px rgba(255,255,255,0.2)",
      }}
      aria-hidden="true"
      data-testid="reddy-badge"
    >
      <div
        className="w-full h-full rounded-full overflow-hidden flex items-center justify-center"
        style={{
          background:
            "radial-gradient(circle at 50% 40%, #0b1a33 0%, #050a18 100%)",
          boxShadow: "inset 0 2px 6px rgba(0,0,0,0.55)",
        }}
      >
        <img
          src="/brand/reddy-club-logo.png"
          alt="Reddy Club"
          className="w-full h-full object-contain select-none"
          draggable={false}
          style={{ filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.6))" }}
        />
      </div>
    </div>
  );
}

/** Small neon "ROYAL" pill — cyan border + gold text, matches theme. */
function RoyalPill() {
  return (
    <span
      className="inline-flex items-center px-2 py-[2px] rounded-full text-[9px] font-black tracking-[0.25em] uppercase shrink-0"
      style={{
        background:
          "linear-gradient(135deg, rgba(234,179,8,0.14) 0%, rgba(14,165,233,0.14) 100%)",
        border: "1px solid rgba(34,211,238,0.55)",
        color: "#fde68a",
        boxShadow:
          "0 0 10px rgba(34,211,238,0.35), inset 0 0 6px rgba(251,191,36,0.15)",
        textShadow: "0 0 6px rgba(251,191,36,0.4)",
      }}
    >
      Royal
    </span>
  );
}
